/** Sphere shelves + tag filtering for the template gallery, and shape detection for the
 *  builder's category picker (P17-T06).
 *
 *  Templates arrive stamped with sphere TAGS (media, health, work…) once the tags/manifests
 *  work lands server-side; this module is deliberately defensive — an untagged payload
 *  degrades to a single unlabeled shelf, so the gallery renders exactly like the pre-tags
 *  flat grid. Everything here is pure (no DOM, no I/O) so it unit-tests directly.
 */

/** Anything that may carry sphere tags — templates today, domains tomorrow. */
export interface Tagged {
  tags?: string[]
}

/** Backend law for a single tag: lowercase kebab, ≤24 chars (`_TAG_RE` in domains/router.py).
 *  The validator REJECTS — it never coerces — so every UI entry point must normalize with
 *  this exact slugifier or the whole create/update 422s. Mirrors DomainPanel's TagsEditor. */
export const TAG_SLUG_MAX_LEN = 24

/** 'Real Estate' → 'real-estate': lowercase, non-alphanumeric runs collapse to one hyphen,
 *  leading/trailing hyphens trimmed, capped at 24 chars (re-trimmed so the cap can't leave
 *  a dangling hyphen). Empty/symbol-only input yields '' — callers skip empties. */
export function toTagSlug(word: string): string {
  return word
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, TAG_SLUG_MAX_LEN)
    .replace(/-+$/g, '')
}

/** Canonical sphere order — shelves, filter chips, and builder suggestions render in this
 *  order; tags outside the canon follow alphabetically. */
export const SPHERE_ORDER = ['media', 'health', 'work', 'finance', 'research', 'life'] as const

/** The sphere chips suggested in the builder's tag field. */
export const SPHERE_SUGGESTIONS: readonly string[] = SPHERE_ORDER

/** 'media' → 'Media', 'real_estate' → 'Real Estate'. */
export function shelfLabel(tag: string): string {
  return tag
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ')
}

function tagRank(tag: string): number {
  const i = (SPHERE_ORDER as readonly string[]).indexOf(tag)
  return i === -1 ? SPHERE_ORDER.length : i
}

function compareTags(a: string, b: string): number {
  return tagRank(a) - tagRank(b) || a.localeCompare(b)
}

/** Distinct tags across items — ALL tags, not just shelf homes, so the filter chip row can
 *  reach a template through its second tag. Canonical spheres first, rest alphabetical. */
export function allShelfTags(items: Tagged[]): string[] {
  return [...new Set(items.flatMap((t) => t.tags ?? []))].sort(compareTags)
}

export interface Shelf<T extends Tagged> {
  /** The sphere tag this shelf groups, or null for the untagged remainder. */
  tag: string | null
  label: string
  items: T[]
}

/**
 * Group items into shelves by their FIRST tag — a template with two tags sits on its first
 * shelf only, so nothing appears twice. Untagged items gather on a trailing null shelf:
 * labeled "More" when it shares the gallery with real shelves, unlabeled when it IS the
 * gallery (the pre-tags payload — callers render that as today's flat grid).
 */
export function groupByShelf<T extends Tagged>(items: T[]): Shelf<T>[] {
  const byTag = new Map<string, T[]>()
  const untagged: T[] = []
  for (const item of items) {
    const home = item.tags?.[0]
    if (home) byTag.set(home, [...(byTag.get(home) ?? []), item])
    else untagged.push(item)
  }
  const shelves: Shelf<T>[] = [...byTag.keys()]
    .sort(compareTags)
    .map((tag) => ({ tag, label: shelfLabel(tag), items: byTag.get(tag)! }))
  if (untagged.length > 0) shelves.push({ tag: null, label: shelves.length > 0 ? 'More' : '', items: untagged })
  return shelves
}

/** Any-match tag filter; empty selection = everything (mirrors filterSpringsByTags). */
export function filterByTags<T extends Tagged>(items: T[], selected: string[]): T[] {
  if (selected.length === 0) return items
  const wanted = new Set(selected)
  return items.filter((t) => (t.tags ?? []).some((x) => wanted.has(x)))
}

/**
 * Feature-detect a builder category's shape from a payload that may not carry one yet (the
 * manifests work lands shapes in parallel). Accepts a string `shape`, or an object bearing a
 * string `label`/`key`. Anything else — including today's shapeless catalog — yields null,
 * and the caller keeps the flat category picker.
 */
export function shapeOf(category: unknown): string | null {
  if (typeof category !== 'object' || category === null) return null
  const s = (category as { shape?: unknown }).shape
  if (typeof s === 'string' && s.trim()) return s.trim()
  if (typeof s === 'object' && s !== null) {
    const o = s as { label?: unknown; key?: unknown }
    if (typeof o.label === 'string' && o.label.trim()) return o.label.trim()
    if (typeof o.key === 'string' && o.key.trim()) return o.key.trim()
  }
  return null
}

/**
 * Group builder categories by detected shape, preserving catalog order both across groups
 * (first-seen shape order) and within them. Shapeless categories gather in a trailing null
 * group. If NO category carries a shape, one null group is returned — the caller renders
 * the flat picker exactly as before shapes existed.
 */
export function groupCategoriesByShape<T>(categories: T[]): { shape: string | null; items: T[] }[] {
  const groups: { shape: string | null; items: T[] }[] = []
  const byShape = new Map<string, T[]>()
  const shapeless: T[] = []
  for (const c of categories) {
    const shape = shapeOf(c)
    if (shape === null) {
      shapeless.push(c)
      continue
    }
    if (!byShape.has(shape)) {
      const items: T[] = []
      byShape.set(shape, items)
      groups.push({ shape, items })
    }
    byShape.get(shape)!.push(c)
  }
  if (groups.length === 0) return [{ shape: null, items: categories }]
  if (shapeless.length > 0) groups.push({ shape: null, items: shapeless })
  return groups
}
